<template>
  <gg-card>
    <gg-card-text class="pa-3">
      <div class="d-flex flex-wrap align-center mb-3">
        <h2 class="body-1 mr-auto kvm-card-title">
          <v-icon size="24" color="primary" class="pb-1" aria-hidden="true">mdi-shield-outline</v-icon>
          <span class="ml-2">Firewall</span>
        </h2>
        <gg-btn small tile color="primary" :disabled="!capable || loadingFirst || !!error" @click="openAdd">
          <v-icon left small aria-hidden="true">mdi-plus</v-icon>Add rule
        </gg-btn>
      </div>

      <!-- Backend doesn't serve a firewall for this service. -->
      <v-alert v-if="!capable" type="info" border="left" dense class="mb-0">
        The firewall isn't available for this server yet.
      </v-alert>

      <template v-else>
        <div v-if="loadingFirst" class="py-8 text-center" role="status" aria-live="polite">
          <v-progress-circular indeterminate color="primary" size="36" width="3" />
          <span class="kvm-sr-only">Loading firewall…</span>
        </div>

        <v-alert v-else-if="error" type="error" border="left" dense class="mb-0" role="alert">
          {{ error.message || 'The firewall could not be loaded.' }}
          <div class="mt-2">
            <gg-btn small tile color="white" class="error--text" @click="load()">
              <v-icon left small aria-hidden="true">mdi-refresh</v-icon>Retry
            </gg-btn>
          </div>
        </v-alert>

        <template v-else>
          <!-- Default policy -->
          <div class="d-flex flex-wrap align-center">
            <v-select
              v-model="policyIn" :items="policyOptions" label="Default inbound"
              outlined dense hide-details class="mr-3 mb-2" style="max-width: 180px;"
            />
            <v-select
              v-model="policyOut" :items="policyOptions" label="Default outbound"
              outlined dense hide-details class="mr-3 mb-2" style="max-width: 180px;"
            />
            <v-checkbox
              v-if="policyDropCausing" v-model="policyImmediate"
              label="Reset existing connections"
              dense hide-details class="mt-0 mr-3 mb-2"
            />
            <gg-btn small tile color="primary" class="mb-2"
                    :loading="savingPolicy" :disabled="!policyChanged" @click="savePolicy">
              Save policy
            </gg-btn>
          </div>
          <p class="kvm-help text-caption mt-1 mb-4">
            The firewall is always on and can't be disabled. Provider security rules (e.g. the
            outbound SMTP block) apply regardless of these defaults.
          </p>

          <!-- Rules -->
          <div class="kvm-fw-table">
            <v-simple-table dense class="vds-fw-table">
              <caption class="kvm-sr-only">Firewall rules</caption>
              <thead>
                <tr>
                  <th scope="col" style="width: 60px;">On</th>
                  <th scope="col">Dir</th>
                  <th scope="col">Action</th>
                  <th scope="col">Proto</th>
                  <th scope="col">Source</th>
                  <th scope="col">Destination</th>
                  <th scope="col">Ports</th>
                  <th scope="col">Comment</th>
                  <th scope="col" style="width: 84px;"><span class="kvm-sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="rule in rules" :key="rule.pos">
                  <td data-label="On">
                    <v-switch
                      :input-value="rule.enable" dense hide-details class="mt-0 pt-0 kvm-fw-switch"
                      :label="ruleSwitchLabel(rule)"
                      :loading="busyPos === rule.pos"
                      :disabled="rule.system_managed || busyPos != null"
                      @change="val => toggleRule(rule, val)"
                    />
                  </td>
                  <td data-label="Dir"><v-chip x-small label class="text-uppercase">{{ rule.type }}</v-chip></td>
                  <td data-label="Action">
                    <span class="font-weight-medium kvm-fw-action" :class="'kvm-fw-action--' + actionClass(rule.action)">{{ rule.action || '—' }}</span>
                  </td>
                  <td data-label="Proto">{{ (rule.proto || 'any') }}</td>
                  <td data-label="Source" class="vds-fw-addr">{{ rule.source || '—' }}</td>
                  <td data-label="Destination" class="vds-fw-addr">{{ rule.dest || '—' }}</td>
                  <td data-label="Ports" class="vds-fw-addr">{{ portsLabel(rule) }}</td>
                  <td data-label="Comment">
                    <template v-if="rule.system_managed">
                      <v-icon x-small class="mr-1" aria-hidden="true" title="Provider-managed">mdi-lock</v-icon>
                      <span class="kvm-sr-only">Provider-managed, read-only. </span>
                    </template>
                    <span class="kvm-help">{{ rule.comment || '—' }}</span>
                  </td>
                  <td data-label="Actions" class="text-right">
                    <v-btn icon small :disabled="!canEdit(rule)" :aria-label="'Edit rule: ' + ruleSummary(rule)" title="Edit" @click="openEdit(rule)">
                      <v-icon small aria-hidden="true">mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn icon small :disabled="!canDelete(rule)" :aria-label="'Delete rule: ' + ruleSummary(rule)" title="Delete" @click="confirmDelete(rule)">
                      <v-icon small aria-hidden="true">mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
                <tr v-if="!rules.length">
                  <td colspan="9" class="text-center kvm-help py-4 kvm-empty-cell">No rules yet. Add one to get started.</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </template>
      </template>

      <!-- Add / edit rule dialog -->
      <v-dialog v-model="ruleDialog" max-width="600" persistent scrollable>
        <gg-card>
          <gg-card-text class="pa-4">
            <h2 class="body-1 mb-3 kvm-card-title">{{ editing ? 'Edit rule' : 'Add rule' }}</h2>

            <div class="d-flex flex-wrap">
              <v-select
                v-model="form.type" :items="directionOptions" label="Direction"
                outlined dense hide-details class="mr-3 mb-3" style="max-width: 160px;"
              />
              <v-select
                v-model="form.action" :items="actionOptions" label="Action"
                outlined dense hide-details class="mr-3 mb-3" style="max-width: 160px;"
              />
              <v-select
                v-model="form.proto" :items="protoOptions" label="Protocol"
                outlined dense hide-details class="mb-3" style="max-width: 160px;"
              />
            </div>

            <div class="d-flex flex-wrap">
              <v-text-field
                v-model="form.source" label="Source" placeholder="IP / CIDR / list"
                outlined dense hide-details="auto" class="mr-3 mb-3" style="max-width: 220px;"
              />
              <v-text-field
                v-model="form.dest" label="Destination" placeholder="IP / CIDR / list"
                outlined dense hide-details="auto" class="mb-3" style="max-width: 220px;"
              />
            </div>

            <div class="d-flex flex-wrap">
              <v-text-field
                v-model="form.dport" label="Destination port" placeholder="443, 8000:8100"
                :disabled="!portsAllowed" outlined dense hide-details="auto"
                class="mr-3 mb-3" style="max-width: 220px;"
              />
              <v-text-field
                v-model="form.sport" label="Source port" placeholder="optional"
                :disabled="!portsAllowed" outlined dense hide-details="auto"
                class="mb-3" style="max-width: 220px;"
              />
            </div>
            <p v-if="!portsAllowed" class="kvm-help text-caption mt-n2 mb-3">
              Ports apply to TCP/UDP only.
            </p>

            <v-text-field
              v-model="form.comment" label="Comment" :counter="255" :rules="commentRules"
              outlined dense hide-details="auto" class="mb-2"
            />
            <v-checkbox v-model="form.enable" label="Enabled" dense hide-details class="mt-1" />
            <v-checkbox
              v-if="ruleDropCausing" v-model="form.immediate"
              label="Also reset the VM's existing connections"
              dense hide-details class="mt-1"
            />
            <p v-if="ruleDropCausing" class="kvm-help text-caption mt-1 mb-0">
              Blocking rules normally affect new connections only; this also drops already-open ones.
            </p>

            <div class="text-right mt-4">
              <gg-btn text class="mr-2" :disabled="saving" @click="ruleDialog = false">Cancel</gg-btn>
              <gg-btn tile color="primary" :loading="saving" :disabled="!formValid" @click="saveRule">
                {{ editing ? 'Save changes' : 'Add rule' }}
              </gg-btn>
            </div>
          </gg-card-text>
        </gg-card>
      </v-dialog>

      <!-- Delete confirm -->
      <v-dialog v-model="deleteDialog" max-width="420">
        <gg-card>
          <gg-card-text class="pa-4">
            <h2 class="body-1 mb-2 kvm-card-title">Delete rule?</h2>
            <p class="mb-4">
              This removes the {{ deleteTarget && deleteTarget.type }} rule
              <strong>{{ deleteTarget && (deleteTarget.action || '') }}</strong
              ><span v-if="deleteTarget && deleteTarget.comment"> ({{ deleteTarget.comment }})</span>.
            </p>
            <v-checkbox
              v-if="deleteDropCausing" v-model="deleteImmediate"
              label="Also reset the VM's existing connections"
              dense hide-details class="mb-4 mt-0"
            />
            <div class="text-right">
              <gg-btn text class="mr-2" :disabled="saving" @click="deleteDialog = false">Cancel</gg-btn>
              <gg-btn tile color="error" :loading="saving" @click="doDelete">Delete</gg-btn>
            </div>
          </gg-card-text>
        </gg-card>
      </v-dialog>
    </gg-card-text>
  </gg-card>
</template>

<script>
import { mapActions } from 'vuex'
import kvm from '@/api/kvm'

const POLICIES = ['ACCEPT', 'DROP', 'REJECT']

const emptyForm = () => ({
  type: 'in',
  action: 'ACCEPT',
  proto: 'any',
  dport: '',
  sport: '',
  source: '',
  dest: '',
  enable: true,
  comment: '',
  immediate: false, // also drop already-open connections (drop-causing changes only)
})

export default {
  name: 'HostingKvmFirewall',
  props: {
    // Provided by KvmServiceView via <router-view :server :loading>.
    server: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
  },
  data: () => ({
    fwLoading: false,
    error: null,
    fw: null, // { enable, policy_in, policy_out, rules }
    policyIn: 'DROP',
    policyOut: 'ACCEPT',
    savingPolicy: false,
    policyImmediate: false, // also drop existing connections on a blocking policy
    deleteImmediate: false, // also drop existing connections when removing a rule
    busyPos: null, // pos of the rule whose toggle is in flight
    // Add/edit dialog
    ruleDialog: false,
    editing: false,
    editPos: null,
    form: emptyForm(),
    saving: false,
    // Delete dialog
    deleteDialog: false,
    deleteTarget: null,
    policyOptions: POLICIES,
    directionOptions: [{ text: 'Inbound', value: 'in' }, { text: 'Outbound', value: 'out' }],
    actionOptions: POLICIES,
    protoOptions: [{ text: 'Any', value: 'any' }, { text: 'TCP', value: 'tcp' }, { text: 'UDP', value: 'udp' }, { text: 'ICMP', value: 'icmp' }],
  }),
  computed: {
    capable () {
      return !!(this.server.capabilities && this.server.capabilities.firewall)
    },
    // Show a spinner only on the first load, not on each mutation re-fetch.
    loadingFirst () {
      return this.fwLoading && !this.fw
    },
    rules () {
      return (this.fw && this.fw.rules) || []
    },
    policyChanged () {
      return !!this.fw && (this.policyIn !== this.fw.policy_in || this.policyOut !== this.fw.policy_out)
    },
    portsAllowed () {
      return this.form.proto === 'tcp' || this.form.proto === 'udp'
    },
    // A blocking rule (DROP/REJECT) is what makes "clear existing connections" relevant.
    ruleDropCausing () {
      return this.form.action === 'DROP' || this.form.action === 'REJECT'
    },
    // A DROP/REJECT default policy is the drop-causing case for the policy save.
    policyDropCausing () {
      return ['DROP', 'REJECT'].includes(this.policyIn) || ['DROP', 'REJECT'].includes(this.policyOut)
    },
    // Removing an active ACCEPT rule can newly drop traffic.
    deleteDropCausing () {
      return !!this.deleteTarget && this.deleteTarget.action === 'ACCEPT' && this.deleteTarget.enable !== false
    },
    commentRules () {
      return [
        v => !v || v.length <= 255 || 'At most 255 characters.',
        v => !v || !v.trim().toLowerCase().startsWith('sys:') || 'Comment can’t start with "sys:".',
      ]
    },
    formValid () {
      if (!this.form.type || !this.form.action) return false
      const c = (this.form.comment || '').trim()
      if (c.length > 255) return false
      if (c.toLowerCase().startsWith('sys:')) return false
      return true
    },
  },
  watch: {
    'server.id': { immediate: true, handler () { this.load() } },
  },
  methods: {
    ...mapActions(['reportSuccess', 'reportError']),
    async load () {
      if (!this.capable || !this.server.id) return
      try {
        this.fwLoading = true
        this.error = null
        const fw = await kvm.getFirewall(this.server.id)
        this.fw = fw
        this.policyIn = fw.policy_in || 'DROP'
        this.policyOut = fw.policy_out || 'ACCEPT'
      } catch (e) {
        this.error = e
      } finally {
        this.fwLoading = false
      }
    },
    // Map an action to a contrast-safe (both-theme) colour class for its label.
    actionClass (action) {
      if (action === 'ACCEPT') return 'accept'
      if (action === 'REJECT') return 'reject'
      return 'drop' // DROP (and group refs)
    },
    // Plain-language summary of a rule for accessible names (switch / icon buttons).
    ruleSummary (rule) {
      const dir = rule.type === 'out' ? 'outbound' : rule.type === 'in' ? 'inbound' : (rule.type || '')
      let s = [dir, (rule.action || '').toLowerCase()].filter(Boolean).join(' ').trim() || 'rule'
      if (rule.comment) s += ` (${rule.comment})`
      return s
    },
    ruleSwitchLabel (rule) {
      return 'Enable ' + this.ruleSummary(rule)
    },
    portsLabel (rule) {
      const parts = []
      if (rule.dport) parts.push(`dport ${rule.dport}`)
      if (rule.sport) parts.push(`sport ${rule.sport}`)
      return parts.length ? parts.join(', ') : '—'
    },
    canEdit (rule) {
      return !rule.system_managed && rule.type !== 'group'
    },
    canDelete (rule) {
      return !rule.system_managed
    },
    async savePolicy () {
      try {
        this.savingPolicy = true
        const res = await kvm.setFirewallPolicy(this.server.id, {
          policy_in: this.policyIn,
          policy_out: this.policyOut,
          immediate: this.policyDropCausing && this.policyImmediate,
        })
        this.reportSuccess(this.mutationToast('Firewall policy updated.', res))
        this.policyImmediate = false
        await this.load()
      } catch (e) {
        this.reportError(e)
      } finally {
        this.savingPolicy = false
      }
    },
    async toggleRule (rule, val) {
      try {
        this.busyPos = rule.pos
        await kvm.updateFirewallRule(this.server.id, rule.pos, { enable: val })
        await this.load()
      } catch (e) {
        this.reportError(e)
        await this.load() // snap the switch back to the server's truth
      } finally {
        this.busyPos = null
      }
    },
    openAdd () {
      this.editing = false
      this.editPos = null
      this.form = emptyForm()
      this.ruleDialog = true
    },
    openEdit (rule) {
      this.editing = true
      this.editPos = rule.pos
      this.form = {
        type: rule.type,
        action: rule.action || 'ACCEPT',
        proto: rule.proto || 'any',
        dport: rule.dport || '',
        sport: rule.sport || '',
        source: rule.source || '',
        dest: rule.dest || '',
        enable: rule.enable !== false,
        comment: rule.comment || '',
      }
      this.ruleDialog = true
    },
    buildPayload () {
      const t = v => {
        const s = v == null ? '' : String(v).trim()
        return s || null
      }
      const ports = this.portsAllowed
      return {
        type: this.form.type,
        action: this.form.action,
        proto: this.form.proto && this.form.proto !== 'any' ? this.form.proto : null,
        dport: ports ? t(this.form.dport) : null,
        sport: ports ? t(this.form.sport) : null,
        source: t(this.form.source),
        dest: t(this.form.dest),
        enable: this.form.enable,
        comment: t(this.form.comment),
        // Only meaningful for blocking rules; backend ignores it otherwise.
        immediate: this.ruleDropCausing && !!this.form.immediate,
      }
    },
    // Append a note when the backend reports it reset live connections.
    mutationToast (base, res) {
      return base + (res && res.connections_cleared ? ' Existing connections were reset.' : '')
    },
    async saveRule () {
      if (!this.formValid) return
      try {
        this.saving = true
        const payload = this.buildPayload()
        if (this.editing) {
          const res = await kvm.updateFirewallRule(this.server.id, this.editPos, payload)
          this.reportSuccess(this.mutationToast('Rule updated.', res))
        } else {
          const res = await kvm.addFirewallRule(this.server.id, payload)
          this.reportSuccess(this.mutationToast('Rule added.', res))
        }
        this.ruleDialog = false
        await this.load()
      } catch (e) {
        this.reportError(e)
      } finally {
        this.saving = false
      }
    },
    confirmDelete (rule) {
      this.deleteTarget = rule
      this.deleteImmediate = false
      this.deleteDialog = true
    },
    async doDelete () {
      try {
        this.saving = true
        const immediate = this.deleteDropCausing && this.deleteImmediate
        const res = await kvm.deleteFirewallRule(this.server.id, this.deleteTarget.pos, immediate)
        this.reportSuccess(this.mutationToast('Rule deleted.', res))
        this.deleteDialog = false
        this.deleteTarget = null
        await this.load()
      } catch (e) {
        this.reportError(e)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.kvm-sr-only {
  @include kvm-sr-only;
}

// Section / dialog titles are real headings now; keep the body-1 look.
.kvm-card-title {
  margin: 0;
}

// Contrast-safe replacement for faint grey helper / comment text.
.kvm-help {
  @include kvm-secondary-text;
}

.vds-fw-table th {
  white-space: nowrap;
}
.vds-fw-addr {
  font-family: monospace;
  font-size: 0.8rem;
  white-space: nowrap;
}

// Action labels — AA contrast on the card surface in BOTH themes.
.kvm-fw-action--accept {
  color: $kvm-focus-light; // #0a7c3c ~5.3:1 on #fff
  .theme--dark & {
    color: $kvm-focus-dark; // #3db14e ~5.6:1 on #002838
  }
}
.kvm-fw-action--reject {
  color: #806600; // dark gold ~5.5:1 on #fff
  .theme--dark & {
    color: #ffb74d; // ~8.9:1 on #002838
  }
}
.kvm-fw-action--drop {
  color: #c62828; // dark red ~5.6:1 on #fff
  .theme--dark & {
    color: #ff8a80; // ~6.8:1 on #002838
  }
}

// Per-row enable switch keeps an accessible name but hides the visible label.
.kvm-fw-switch ::v-deep .v-label {
  @include kvm-sr-only;
}

// Mobile: stack the rules table into cards; neutralise the off-brand teal stripe.
.kvm-fw-table {
  @include kvm-stacked-table;
  @include kvm-table-destripe;

  @media (max-width: $kvm-bp-mobile) {
    ::v-deep td.kvm-empty-cell {
      justify-content: center;
      text-align: center;
    }
    ::v-deep td.kvm-empty-cell::before {
      content: none;
    }
  }
}

// Visible focus ring for the inline edit/delete buttons (global focus is too weak).
.kvm-fw-table ::v-deep .v-btn:focus-visible {
  @include kvm-focus-ring;
}
</style>
